import { useEffect, useMemo, useState } from 'react'
import { MAX_QUANTITY_PER_ITEM } from '@cannect/services/resources/recurrency/constants'
import { useSubscriptionPreview } from '@cannect/services/resources/recurrency/hooks'
import type { SubscriptionDetail, SubscriptionItem } from '@cannect/services/resources/recurrency/types'

const PREVIEW_DEBOUNCE_MS = 400

export const maxQuantityFor = (item: Pick<SubscriptionItem, 'allowedQuantityPerCycle'>) =>
  Math.min(MAX_QUANTITY_PER_ITEM, item.allowedQuantityPerCycle ?? MAX_QUANTITY_PER_ITEM)

export const activeItems = (detail: Pick<SubscriptionDetail, 'items'>) =>
  detail.items.filter((item) => item.active !== false)

const initialQuantities = (detail: SubscriptionDetail) =>
  Object.fromEntries(activeItems(detail).map((item) => [item.productId, item.quantity])) as Record<number, number>

// Rascunho de quantidade e intervalo (data-model §6): preview com debounce e
// um único PATCH só com o que mudou.
export const useEditDraft = (detail: SubscriptionDetail) => {
  const [quantities, setQuantities] = useState(() => initialQuantities(detail))
  const [cycleDays, setCycleDays] = useState(detail.cycleDays)

  useEffect(() => {
    setQuantities(initialQuantities(detail))
    setCycleDays(detail.cycleDays)
  }, [detail])

  const items = activeItems(detail)
  const changedItems = items
    .filter((item) => quantities[item.productId] !== undefined && quantities[item.productId] !== item.quantity)
    .map((item) => ({ productId: item.productId, quantity: quantities[item.productId] }))
  const cycleChanged = cycleDays !== detail.cycleDays
  const dirty = changedItems.length > 0 || cycleChanged

  const previewBody = useMemo(
    () =>
      dirty
        ? {
            items: items.map((item) => ({
              productId: item.productId,
              quantity: quantities[item.productId] ?? item.quantity
            })),
            cycleDays
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dirty, JSON.stringify(quantities), cycleDays, detail]
  )

  const [debouncedBody, setDebouncedBody] = useState(previewBody)
  useEffect(() => {
    if (!previewBody) {
      setDebouncedBody(null)
      return undefined
    }
    const timer = setTimeout(() => setDebouncedBody(previewBody), PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [previewBody])

  const preview = useSubscriptionPreview(detail.id, debouncedBody)

  const patchBody = {
    ...(changedItems.length ? { items: changedItems } : {}),
    ...(cycleChanged ? { cycleDays } : {})
  }

  const reset = () => {
    setQuantities(initialQuantities(detail))
    setCycleDays(detail.cycleDays)
  }

  const setQuantity = (productId: number, quantity: number) =>
    setQuantities((current) => ({ ...current, [productId]: quantity }))

  return {
    quantities,
    cycleDays,
    setQuantity,
    setCycleDays,
    dirty,
    changedItems,
    cycleChanged,
    patchBody,
    preview,
    previewPending: dirty && (debouncedBody !== previewBody || preview.isFetching),
    reset
  }
}
